import { useRouteNavigator } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import { api } from '../../api/client';
import { usePagedLoader } from '../../api/paging';
import {
  DORM_LABELS,
  STUDY_CLASS_LABELS,
  type Dorm,
  type Page,
  type StudyClass,
} from '../../api/types';
import { REGISTRY_TABS, REGISTRY_TAB_PARAM, type RegistryTab } from '../../app/router';
import { t } from '../../i18n';
import { useKeptState } from '../../shared/lib/keptState';
import type { ApiImage } from '../../shared/lib/media';
import { useSearchTerm } from '../../shared/lib/search';
import {
  Action,
  CardHeading,
  CardLink,
  CardText,
  ChipRow,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterSelect,
  Gap,
  Gutter,
  LoadingState,
  Meta,
  Notice,
  Screen,
  SearchField,
  Section,
  TabRow,
  Tag,
  Thumb,
  TileGrid,
  type FilterOption,
  type TabRowOption,
} from '../../shared/ui';

/**
 * Строка GET /api/registry. Описана здесь, а не в api/types.ts: её читает
 * только этот экран. Поля — зеркало RegistryListItemView.
 */
interface RegistryListItem {
  id: number;
  dorm: Dorm | null;
  studyClass: StudyClass | null;
  nameRu: string | null;
  nameEn: string | null;
  nicknames: string | null;
  race: string | null;
  appearanceImage: ApiImage | null;
}

const DORM_FILTERS: readonly Dorm[] = ['o1', 'o2', 'o3'];

/** Пустая строка фильтром быть не может: это ключ вкладки. */
type DormFilter = Dorm | 'all';

const TAB_LABELS: Record<RegistryTab, string> = {
  personnel: t('registry.tab.personnel'),
  alumni: t('registry.tab.alumni'),
  resident: t('registry.tab.resident'),
  professor: t('registry.tab.professor'),
};

const TABS: readonly TabRowOption<RegistryTab>[] = REGISTRY_TABS.map((tab) => ({
  value: tab,
  label: TAB_LABELS[tab],
}));

const DORM_OPTIONS: readonly FilterOption<DormFilter>[] = [
  { value: 'all', label: t('common.empty.all') },
  ...DORM_FILTERS.map((dorm): FilterOption<DormFilter> => ({
    value: dorm,
    label: DORM_LABELS[dorm],
  })),
];

const EMPTY_TITLES: Record<RegistryTab, string> = {
  personnel: t('registry.empty.tab.personnel'),
  alumni: t('registry.empty.tab.alumni'),
  resident: t('registry.empty.tab.resident'),
  professor: t('registry.empty.tab.professor'),
};

export function RegistryPanel({ id }: NavIdProps) {
  const routeNavigator = useRouteNavigator();

  const [tab, setTab] = useKeptState<RegistryTab>('registry.tab', readTabFromUrl);
  const [dorm, setDorm] = useKeptState<DormFilter>('registry.dorm', 'all');
  const search = useSearchTerm('registry.search');

  const registry = usePagedLoader<RegistryListItem>(
    (params, signal) =>
      api.get<Page<RegistryListItem>>(
        '/api/registry',
        {
          tab,
          dorm: dorm === 'all' ? undefined : dorm,
          q: search.term || undefined,
          limit: params.limit,
          offset: params.offset,
        },
        signal,
      ),
    [tab, dorm, search.term],
    true,
    'registry',
  );

  // Строки прошлой вкладки к новой не относятся: под ней они читались бы как
  // несработавший фильтр.
  const page = registry.leftover ? null : registry.data;
  const items = page?.items ?? [];
  // Дочитывание идёт — кнопка остаётся на месте со спиннером, а не исчезает.
  const waiting =
    registry.loadMore === null && registry.loading && page !== null && items.length < page.total;

  /*
    Счёт живёт в шапке, а не строкой над сеткой: строка уезжает вместе
    со списком, а шапка остаётся на месте, пока его листают.
  */
  return (
    <Screen
      id={id}
      title={t('registry.title')}
      count={page === null ? null : page.total}
      unit="dossier"
    >
      <Section>
        <TabRow options={TABS} value={tab} onChange={setTab} />

        {/* Поиск стоит между полосами: вкладка задаёт, ЧТО за список,
            общежитие — уже срез внутри найденного. */}
        <SearchField
          value={search.input}
          onChange={search.setInput}
          placeholder={t('registry.search.placeholder')}
        />

        <FilterBar>
          <FilterSelect
            label={t('registry.filter.dorm')}
            options={DORM_OPTIONS}
            value={dorm}
            onChange={setDorm}
          />
        </FilterBar>
      </Section>

      <Section>
        {registry.loading && page === null ? (
          <LoadingState text={t('registry.loading.list')} />
        ) : null}

        {registry.error !== null && items.length === 0 ? (
          <ErrorState message={registry.error} onRetry={registry.reload} />
        ) : null}

        {page !== null && registry.error === null && items.length === 0 ? (
          <EmptyState
            title={search.term ? t('registry.empty.notFound') : EMPTY_TITLES[tab]}
            action={
              search.term ? (
                <Action tone="quiet" size="m" onClick={() => search.setInput('')}>
                  {t('registry.search.clear')}
                </Action>
              ) : null
            }
          >
            {search.term ? t('registry.empty.notFoundHint') : t('registry.empty.hint')}
          </EmptyState>
        ) : null}

        {items.length > 0 ? (
          <TileGrid>
            {items.map((item) => (
              <RegistryTile
                key={item.id}
                item={item}
                onOpen={() => routeNavigator.push(`/character/${item.id}`)}
              />
            ))}
          </TileGrid>
        ) : null}

        {registry.loadMore !== null || waiting ? (
          <Gutter>
            <Action
              tone="quiet"
              size="l"
              stretched
              loading={waiting}
              onClick={registry.loadMore ?? undefined}
            >
              {t('common.action.showMore')}
            </Action>
          </Gutter>
        ) : null}

        {registry.error !== null && items.length > 0 ? (
          <Gutter>
            <Notice
              tone="error"
              action={
                registry.loadMore === null ? (
                  <Action tone="quiet" size="s" onClick={registry.reload}>
                    {t('common.action.retry')}
                  </Action>
                ) : undefined
              }
            >
              {registry.error}
            </Notice>
          </Gutter>
        ) : null}
      </Section>
    </Screen>
  );
}

/**
 * Плитка досье: портрет во всю ширину колонки, под ним имя и факты.
 *
 * Портрет идёт от края и тянется за колонкой, а не стоит квадратом на 128 px
 * посреди полей: ради внешности в реестр и заходят. Отсюда padded={false} и
 * свой <Gutter> под текстом — отступы держит он, а не карточка.
 *
 * Общежитие и класс — метками ПОВЕРХ портрета, парой: это то же, чем
 * персонаж представляется в досье, и разводить их по двум местам плитки
 * незачем. Обе залиты, потому что лежат на фотографии: контур на пёстром
 * кадре не читается. Под именем остаётся раса — она одна и не метка.
 * Просмотра на весь экран у плитки нет: Thumb с галереей — это кнопка, а она
 * оказалась бы внутри кнопки CardLink.
 */
function RegistryTile({ item, onOpen }: { item: RegistryListItem; onOpen: () => void }) {
  const name = displayName(item);

  return (
    <CardLink
      onClick={onOpen}
      label={t('registry.action.openDossier', { name })}
      tone="published"
      padded={false}
    >
      <Thumb
        image={item.appearanceImage}
        fill
        ratio="wide"
        alt={name}
        overlay={
          item.dorm === null && item.studyClass === null ? undefined : (
            <ChipRow>
              {item.dorm === null ? null : (
                <Tag tone="success" fill>
                  {DORM_LABELS[item.dorm]}
                </Tag>
              )}
              {item.studyClass === null ? null : (
                <Tag tone="error" fill>
                  {t('registry.card.studyClass', { value: STUDY_CLASS_LABELS[item.studyClass] })}
                </Tag>
              )}
            </ChipRow>
          )
        }
      />
      <CardText>
        <CardHeading>{name}</CardHeading>
        <Gap step="s1h" />
        <Meta tone="dim" lead>
          {facts(item)}
        </Meta>
      </CardText>
    </CardLink>
  );
}

/** Имя для списка: русское, английское, обращение — что первым нашлось. */
function displayName(item: RegistryListItem): string {
  return item.nameRu ?? item.nameEn ?? item.nicknames ?? t('common.empty.noName');
}

/** Общежитие и класс стоят метками на портрете — под именем остаётся только раса. */
function facts(item: RegistryListItem): (string | null)[] {
  return [item.race === '' ? null : item.race];
}

/**
 * Вкладку из адреса читаем один раз за жизнь документа — это прямая ссылка вида
 * /?tab=alumni; дальше она живёт в памяти экрана. Обратно в адрес не пишем:
 * переключение вкладки не должно оставлять следов в истории, иначе «назад» в ВК
 * начнёт отматывать клики по табам.
 */
function readTabFromUrl(): RegistryTab {
  const hash = window.location.hash;
  const at = hash.indexOf('?');
  if (at < 0) return 'personnel';

  const value = new URLSearchParams(hash.slice(at + 1)).get(REGISTRY_TAB_PARAM);
  if (!value) return 'personnel';

  return REGISTRY_TABS.includes(value as RegistryTab) ? (value as RegistryTab) : 'personnel';
}
